//== state → DOM rendering for the service charges calculator.
//== every attribute is svc-prefixed so nothing here can be reached by the
//== redemption calculator's render.js, or vice versa (docs/new-calculators.md §4).
//==
//==   [data-svc-derive="key"]   reads from the derived lookup (or, inside a
//==                             [data-svc-band] row, from that band's row)
//==   [data-svc-format="type"]  picks the formatter
//==   [data-svc-placeholder]    text shown while the value is null
//==   [data-svc-gate="flag"]    hidden unless the flag is truthy
//==                             (hasResult, noResult,
//==                             originalEquityOverMax, currentEquityOverOriginal)
//==   [data-svc-error="field"]  validation message slot
//==
//== inputs.js owns input field formatting; this module owns read-only output.

import { calculate, originalEquitySharePct, DEFAULT_BANDS } from './calculate.js'
import { validate } from './validate.js'
import { formatMoney, formatMoneyFixed, formatPercent } from '../calc-shared/format.js'

const PLACEHOLDER = '-'

//== formatters expect raw numeric input. "percent" assumes percentage form
//== (10 -> "10.00%") and is what state stores; "percent-decimal" assumes
//== decimal form (0.10 -> "10.00%") and is what calculate() returns - the
//== conversion happens at the render boundary, once.
const FORMATTERS = {
  percent: (value) => formatPercent(value),
  'percent-decimal': (value) => formatPercent(value * 100),
  money: formatMoney,
  //== fixed 2dp for the results table, so a zero-rate band reads €0.00
  //== alongside €54.69 rather than €0
  'money-2dp': (value) => formatMoneyFixed(value),
  plain: (value) => String(value),
}

export function initRender(store, root, config) {
  //== band rows are handled separately, so they're excluded here rather than
  //== matched twice - :not() on the ancestor isn't possible in one selector,
  //== so the split is done in JS
  const bandRows = Array.from(root.querySelectorAll('[data-svc-band]'))
  const targets = Array.from(root.querySelectorAll('[data-svc-derive]')).filter(
    (el) => !el.closest('[data-svc-band]')
  )
  const gateTargets = Array.from(root.querySelectorAll('[data-svc-gate]'))
  const errorTargets = Array.from(root.querySelectorAll('[data-svc-error]'))

  function render(state) {
    //== results are gated on the form being valid, not merely on calculate()
    //== returning a figure: calculate() needs only the purchase price and the
    //== current share, but all three fields are required, and a figure shown
    //== next to a validation error reads as though the bad input was accepted.
    //==
    //== validated once and passed down - the messages go to renderErrors, and
    //== the equity-cap callout needs to know whether that field is already in
    //== error before it warns as well.
    const { valid, errors } = validate(state)
    const result = valid ? calculate({ inputs: state.inputs, config }) : null
    const lookup = withExtras(state, result, config, errors)

    targets.forEach((el) => {
      writeOutput(el, lookup[el.dataset.svcDerive], formatterFor(el))
    })

    renderBands(bandRows, result, config)
    applyGates(gateTargets, lookup)
    renderErrors(root, errorTargets, state, errors)
  }

  store.subscribe(render)
  render(store.getState())
}

//== one row per rate band. labels and rates come from config whether or not
//== the customer's inputs are complete, so the table always shows the rates
//== that apply; the two money cells stay on their placeholder until there is
//== a result. surplus rows in the markup are hidden rather than left showing
//== a band the config no longer has.
function renderBands(rows, result, config) {
  const bands = config?.bands?.length ? config.bands : DEFAULT_BANDS
  rows.forEach((row) => {
    const index = Number(row.dataset.svcBand)
    const band = Number.isInteger(index) ? bands[index] : null
    row.hidden = !band
    if (!band) return

    const calculated = result?.rows?.[index] ?? null
    const values = {
      bandLabel: band.label ?? '',
      bandRate: Number.isFinite(band.rate) ? band.rate : null,
      perMonth: calculated?.perMonth ?? null,
      perYear: calculated?.perYear ?? null,
    }

    row.querySelectorAll('[data-svc-derive]').forEach((el) => {
      writeOutput(el, values[el.dataset.svcDerive], formatterFor(el))
    })
  })
}

//== display-only additions on top of calculate()'s output.
//== originalEquityPct has a partial-input fallback: calculate() returns null
//== until the current share is entered too, but the derived original share only
//== needs the two money fields, so the step-1 row populates as soon as they are
//== both in. same pattern as the redemption calculator's withExtras.
function withExtras(state, result, config, errors) {
  const inputs = state?.inputs ?? {}
  const originalEquityPct = result?.originalEquityPct ?? originalEquitySharePct(inputs)
  return {
    ...(result ?? {}),
    originalEquityPct,
    currentEquityValue: result?.currentEquityValue ?? null,
    //== warning flag only - unlike the redemption calculator this cap does not
    //== block anything, because the original equity share is display-only here
    //== and feeds no figure in the results table.
    originalEquityOverMax: computeOriginalEquityOverMax(originalEquityPct, config, errors),
    //== warning flag only. unlike the cap above, this one's input does feed
    //== every figure in the table - see plan doc §10 item 2.
    currentEquityOverOriginal: computeCurrentEquityOverOriginal(inputs, originalEquityPct),
    //== gates: which of the two states the results panel is in
    hasResult: result ? true : null,
    noResult: result ? null : true,
  }
}

//== true when the derived original share (decimal) exceeds config.maxEquityPct,
//== else null so the callout stays hidden under the applyGates missing/ready
//== rule. no cap configured -> never warn.
//==
//== suppressed while the original equity field carries a validation error, so
//== an amount above the purchase price shows only that field's message and not
//== both (decided in review 2026-08-28, plan doc §10 item 4). keyed on the error
//== rather than on a repeated 100% threshold, so validate.js stays the only
//== place that decides what is wrong with this field.
function computeOriginalEquityOverMax(originalEquityPct, config, errors) {
  if (errors?.originalEquity) return null
  const cap = config?.maxEquityPct
  if (!Number.isFinite(cap) || !Number.isFinite(originalEquityPct)) return null
  return originalEquityPct > cap ? true : null
}

//== true when the entered current share exceeds the original, else null.
//== currentEquityPct is stored in percentage form (10), originalEquityPct in
//== decimal (0.1), so the latter is converted before comparing.
//==
//== the comparison is against the original share rounded to the 2dp the page
//== displays, not its full precision. the caption tells a customer who has not
//== redeemed to re-enter the figure shown above, so an original share of, say,
//== 9.995% - displayed as 10.00% - would otherwise warn against the very value
//== the page asked for. the warning has to agree with what is on screen.
function computeCurrentEquityOverOriginal(inputs, originalEquityPct) {
  const current = inputs?.currentEquityPct
  if (!Number.isFinite(current) || !Number.isFinite(originalEquityPct)) return null
  const originalDisplayedPct = Math.round(originalEquityPct * 10000) / 100
  return current > originalDisplayedPct ? true : null
}

function formatterFor(el) {
  const formatType = el.dataset.svcFormat ?? 'plain'
  return FORMATTERS[formatType] ?? FORMATTERS.plain
}

function writeOutput(el, value, formatter) {
  const isInput = el.tagName === 'INPUT'
  const isMissing =
    value == null || value === '' || (typeof value === 'number' && !Number.isFinite(value))
  if (isMissing) {
    if (isInput) el.value = ''
    else el.textContent = el.dataset.svcPlaceholder ?? PLACEHOLDER
    return
  }
  const formatted = formatter(value)
  if (isInput) el.value = formatted
  else el.textContent = formatted
}

//== show / hide on a truthy flag in the lookup. null hides, true shows -
//== deliberately not a boolean pair so an unknown flag name fails closed.
function applyGates(targets, lookup) {
  targets.forEach((el) => {
    el.hidden = !lookup[el.dataset.svcGate]
  })
}

//== validation messages, gated per field on the customer having left that
//== field. a blank form shows no errors; leaving one field empty flags only
//== that field.
function renderErrors(root, targets, state, errors) {
  if (!targets.length) return
  const touched = state?.touched ?? {}
  targets.forEach((el) => {
    const field = el.dataset.svcError
    const msg = touched[field] ? errors[field] : null
    if (msg) {
      //== set hidden=false before content so aria-live="polite" picks up the
      //== text change while the element is in the accessibility tree
      el.hidden = false
      el.textContent = msg
    } else {
      el.hidden = true
    }
    markInvalid(root, el, !!msg)
  })
}

//== mirror the error state onto the input itself, so assistive tech reports the
//== field as invalid rather than relying on the live-region message alone.
//== the input is found by reading back the aria-describedby the markup already
//== points at this message, so the input ↔ message pairing lives in exactly one
//== place and no field-name-to-selector map is duplicated here. re-queried on
//== every render because inputs.js replaces input nodes on Start Over.
function markInvalid(root, errorEl, isInvalid) {
  if (!errorEl.id) return
  const input = root.querySelector(`[aria-describedby~="${errorEl.id}"]`)
  if (!input) return
  if (isInvalid) input.setAttribute('aria-invalid', 'true')
  else input.removeAttribute('aria-invalid')
}
